
import React, { useEffect, useState } from "react";
import WarehouseCard from "./StepOne";
import AssignmentStep from "./AssignmentStep"; 
import { useNavigate, useParams } from "react-router-dom";
import {
  useOrderTrackStore,
  OrderDetail as IOrderDetail,
  TrackingData,
} from "../store/OrderStore";
import axiosApi from "../server/service";
import toast from "react-hot-toast";
import { ArchiveBoxIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "lucide-react";
import  { ConfirmationModal } from '../component/ConfirmationModal';




interface OrderData {
  _id: string;
  lotNumber: string;
  designId: {
    _id: string;
    basicInfo: {
      designNo: string;
      designCategory: string;
    };
  };
  orderDetails: IOrderDetail[];
  totalQuantity: number;
  grandTotalPrice: number;
  trackingData?: TrackingData;
}





const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

const OrderSteps: React.FC = () => {
  // Updated steps for clarity: Step 2 handles Master/Job-work, Step 3 handles Karigar/Group-work
  const steps = [
    "Taka Arrival",
    "Master/Job Work Assignment",
    "Karigar Assignment",

  ];
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [loading, setLoading] = useState(true);
  const { id: orderId } = useParams<{ id: string }>();
  const [isMarkingDone, setIsMarkingDone] = useState(false); // Naya state for Done button

  const loadOrder = useOrderTrackStore((state) => state.loadOrder);
  const reset = useOrderTrackStore((state) => state.reset);
  const getSavePayload = useOrderTrackStore((state) => state.getSavePayload);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [isSaving, setIsSaving] = useState(false);


    const fetchOrderDetails = async () => {
      setLoading(true);

      reset();
    if (!orderId) {
      toast.error("Order ID is missing.");
      navigate("/ordermanagement");
      return;
    }
      try {
        const response = await axiosApi.get(
          `/api/orders/getOrderById/${orderId}`
        );
        setOrderData(response.data.data);
        loadOrder(response.data.data);
      } catch (error) {
        console.error("Failed to fetch order details:", error);
        toast.error("Failed to load order details.");
      } finally {
        setLoading(false);
      }
    };


  useEffect(() => {
  fetchOrderDetails();
   return () => {
      reset();
    };
}, [orderId, navigate, loadOrder, reset]);


  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSaveProgress = async () => {
    if (!orderId) {
      toast.error("Order ID not found. Cannot save.");
      return;
    }
    setIsSaving(true);
    const toastId = toast.loading(
      "Saving progress and updating assignments..."
    );
    try {
      const payload = getSavePayload();
      // The new endpoint handles saving both Taka (trackingData) and all assignments (orderDetails)
      await axiosApi.put(`/api/orders/update-assignments/${orderId}`, payload);
      toast.success("Progress Saved Successfully!", { id: toastId });
    } catch (error:any) {

      console.error("Failed to save order progress:", error);
      if (error.response && error.response.data && error.response.data.message) {
        // Show the backend's specific error message (e.g., "Duplicate Master assignment...")
        toast.error(error.response.data.message, { id: toastId });
      } else {
        // Show a generic message if the error is something else
        toast.error(
          "Could not save progress. An unexpected error occurred.",
          { id: toastId }
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndFinish = async () => {
    await handleSaveProgress();
    navigate("/ordermanagement");
  };

  const initiateMarkOrderDone = () => {
        if (!orderData?.lotNumber) {
            toast.error("Order data not loaded.");
            return;
        }
        setIsModalOpen(true);
    };

    const handleConfirmMarkOrderDone = async () => {
    if (!orderId) {
      toast.error("Order ID not found.");
      return;
    }

    setIsMarkingDone(true);
    const toastId = toast.loading("Marking order as DONE...");
    setIsModalOpen(false); // Close the modal immediately

    try {
        await handleSaveProgress(); 

        await axiosApi.patch(`/api/orders/mark-done/${orderId}`);
        
        toast.success(`Order ${orderData?.lotNumber} is now DONE!`, { id: toastId });
        
        navigate("/ordermanagement"); 

    } catch (error: any) {
        console.error("Failed to mark order as Done:", error);
        toast.error(error.response?.data?.message || "Failed to mark order as Done.", { id: toastId });
        setIsModalOpen(true); 
    } finally {
        setIsMarkingDone(false);
    }
  };



  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-lg font-semibold text-gray-700">
          Loading Order Details...
        </p>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="flex flex-col justify-center items-center h-screen text-center">
        <p className="text-lg font-semibold text-red-600 mb-4">
          Could not load order details.
        </p>
        <button
          onClick={() => navigate("/ordermanagement")}
          className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
        >
          Back to Orders
        </button>
      </div>
    );
  }
  

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {/* Header (omitted for brevity - remains unchanged) */}
      <div className="flex items-center justify-between mb-5 bg-white p-6 shadow-md rounded-lg border border-gray-200">
        <div className="flex items-center">
          <div className="bg-purple-600 text-white p-3 rounded-xl mr-4 shadow-lg flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M17 12a2 2 0 100-4 2 2 0 000 4zm0 4a2 2 0 100-4 2 2 0 000 4z"
              />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900">
              Order Management
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Order Tracking for Lot: {orderData.lotNumber}
            </p>
          </div>
        </div>
        <button
          className="text-gray-400 hover:text-gray-600 transition-colors"
          onClick={() => navigate("/ordermanagement")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Stepper (omitted for brevity - remains unchanged) */}
      <div className="p-8 bg-gray-50 rounded-lg">
        <p className="text-md font-medium text-gray-700 mb-6">
          Step {currentStep} of {steps.length}
        </p>
        <div className="flex items-center">
          {steps.map((step, index) => {
            const isActive = index + 1 === currentStep;
            const isCompleted = index + 1 < currentStep;
            return (
              <div key={step} className="flex-1 flex items-center">
                <div className="flex flex-col items-center text-center">
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-300 ${
                      isCompleted
                        ? "bg-indigo-600 border-indigo-600 text-white"
                        : isActive
                        ? "border-indigo-600 text-indigo-600 bg-white"
                        : "border-gray-300 text-gray-400 bg-white"
                    }`}
                  >
                    {isCompleted ? "✓" : index + 1}
                  </div>
                  <span
                    className={`mt-2 text-xs font-medium ${
                      isActive
                        ? "text-indigo-600"
                        : isCompleted
                        ? "text-indigo-500"
                        : "text-gray-400"
                    }`}
                  >
                    {step}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className="flex-1 h-1 mx-4 relative">
                    <div className="absolute inset-0 bg-gray-200 rounded-full"></div>
                    <div
                      className={`absolute inset-0 rounded-full transition-all duration-500 ${
                        isCompleted
                          ? "bg-indigo-600 w-full"
                          : isActive
                          ? "bg-indigo-600 w-1/2"
                          : "w-0"
                      }`}
                    ></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Order Summary (omitted for brevity - remains unchanged) */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200/60 p-6 md:p-8 my-8">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900">Order Summary</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-indigo-50 border border-blue-100 rounded-xl p-4">
            <p className="text-xs font-semibold text-blue-600 mb-1">
              LOT NUMBER
            </p>
            <p className="font-bold text-gray-900 text-md">
              {orderData.lotNumber}
            </p>
          </div>
          <div className="bg-sky-50 border border-blue-100 rounded-xl p-4">
            <p className="text-xs font-semibold text-blue-600 mb-1">
              DESIGN NAME
            </p>
            <p className="font-bold text-gray-900 text-md">
              {orderData.designId.basicInfo.designNo}
            </p>
          </div>
          <div className="bg-violet-50 border border-blue-100 rounded-xl p-4">
            <p className="text-xs font-semibold text-blue-600 mb-1">PATTERN</p>
            <p className="font-bold text-gray-900 text-md">
              {orderData.designId.basicInfo.designCategory}
            </p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
            <p className="text-xs font-semibold text-amber-600 mb-1">
              TOTAL PIECES
            </p>
            {orderData.orderDetails.map((detail) => (
              <p
                key={detail.sizeSegmentId._id}
                className="font-bold text-gray-800 text-sm"
              >
                • {detail.sizeSegmentId.segmentName} — {detail.totalPieces}
              </p>
            ))}
          </div>
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 ">
            <p className="text-xs font-semibold text-emerald-600 mb-1">
              TOTAL VALUE
            </p>
            <p className="font-bold text-green-600 text-xl">
              {formatCurrency(orderData.grandTotalPrice)}
            </p>
          </div>
        </div>
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Size Details
          </h3>
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-green-100">
                <tr className="divide-x divide-slate-200">
                  <th className="py-3 px-4 text-left font-semibold text-slate-700">
                    Size
                  </th>
                  <th className="p-3 text-center font-semibold text-slate-700">
                    number of pieces
                  </th>
                  <th className="p-3 text-center font-semibold text-slate-700">
                    Quantity
                  </th>
                  <th className="p-3 text-center font-semibold text-slate-700">
                    Total Quantity
                  </th>

                  <th className="py-3 px-4 text-right font-semibold text-slate-700">
                    Price
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {orderData.orderDetails.map((detail, index) => (
                  <tr
                    key={index}
                    className="divide-x divide-slate-200 hover:bg-slate-50 transition-colors"
                  >
                    <td className="py-3 px-4 font-medium text-slate-800">
                      {detail.sizeSegmentId.segmentName}
                    </td>
                    <td className="py-3  text-center text-slate-600">
                      {detail.sizeSegmentId.numberOfSets}
                    </td>
                    <td className="py-3  text-center text-slate-600">
                      {detail.quantity}
                    </td>
                    <td className="py-3  text-center text-slate-600">
                      {detail.totalPieces}
                    </td>
                    <td className="py-3 px-4 text-right font-semibold text-slate-800">
                      {formatCurrency(detail.totalPrice)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="mt-8">
        {currentStep === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <WarehouseCard title="Mumbai" subtitle="" />
            <WarehouseCard title="Surat" subtitle="" />
          </div>
        )}
        {currentStep === 2 && (
          <AssignmentStep
            title="Master / Job Work Assignment"
            stepNumber={2}
            employeeTypes={["Master", "job-work"]}
            // assignmentKey="masterAssignment" // ✅ Specific assignment key
            onReload={fetchOrderDetails}
          />
        )}
        {currentStep === 3 && (
          <AssignmentStep
            title="Karigar & Group Assignment"
            stepNumber={3}
            employeeTypes={["Karigar","Group Work"]}
            // assignmentKey="karigarAssignment" // ✅ Specific assignment key
            onReload={fetchOrderDetails}
          />
        )}
      </div>



      <div className="flex justify-between items-center mt-10 gap-4">
        <button
          onClick={handlePrev}
          disabled={currentStep === 1}
          className="
              px-8 py-3 
              bg-white 
              text-gray-900 
              font-semibold 
              rounded-xl 
              border border-gray-300 
              shadow-sm 
              hover:bg-gray-100 
              hover:border-gray-400 
              active:scale-[0.97] 
              active:bg-gray-200 
              disabled:opacity-50 
              disabled:cursor-not-allowed 
              transition-all duration-200 ease-out
"
        >
          Previous
        </button>

        <div className="flex items-center gap-3">
          <button
            onClick={handleSaveProgress}
            disabled={isSaving}
            className="flex items-center gap-2 px-6 py-3 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-600 active:bg-emerald-700 disabled:bg-emerald-300 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"
          >
            <ArchiveBoxIcon className="h-5 w-5" />
            {isSaving ? "Saving..." : "Save Progress"}
          </button>

          {currentStep === steps.length && (
             <button
                onClick={initiateMarkOrderDone}
                disabled={isSaving || isMarkingDone} 
                className="flex items-center gap-2 px-8 py-3 text-white font-semibold rounded-lg bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:bg-red-400 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <CheckCircleIcon className="h-5 w-5" />
                {isMarkingDone ? "Marking Done..." : "Mark Order Done"}
              </button>
          )}

          {currentStep === steps.length ? (
            <button
              onClick={handleSaveAndFinish}
              disabled={isSaving}
              className="px-8 py-3 text-white font-semibold rounded-lg bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:bg-green-400 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"
            >
              {isSaving ? "Saving..." : "Save & Finish"}
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="px-8 py-3 text-white font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              Next
            </button>
          )}
        </div>
      </div>

      <ConfirmationModal
          lotNumber={orderData.lotNumber}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleConfirmMarkOrderDone}
          isProcessing={isMarkingDone}
      />

    </div>
  );
};

export default OrderSteps;
